/**
 * ✅ DEBOUNCE UTILITY
 * Reduce múltiples llamadas rápidas a una sola llamada retrasada
 * Útil para: búsquedas, validaciones, redimensionamiento de ventana
 */

/**
 * Crea una versión debounced de una función
 * @param {function} fn - Función a debounce
 * @param {number} delay - Milisegundos a esperar (por defecto 500ms)
 * @returns {function} Función debounced
 */
export function debounce(fn, delay = 500) {
    let timeoutId;
    
    function debounced(...args) {
        // Limpiar timeout anterior
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        
        // Establecer nuevo timeout
        timeoutId = setTimeout(() => {
            fn(...args);
        }, delay);
    }
    
    // Método para cancelar el debounce pendiente
    debounced.cancel = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
    
    // Método para ejecutar inmediatamente
    debounced.flush = (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        fn(...args);
    };
    
    return debounced;
}

/**
 * Hook de React para usar debounce
 * @param {function} callback - Función a ejecutar
 * @param {number} delay - Milisegundos a esperar
 * @param {array} deps - Dependencias (como useEffect)
 * @returns {function} Función debounced
 */
export function useDebounce(callback, delay = 500, deps = []) {
    const React = require('react');
    
    const debouncedRef = React.useRef(debounce(callback, delay));
    
    React.useEffect(() => {
        debouncedRef.current = debounce(callback, delay);
    }, [callback, delay]);
    
    React.useEffect(() => {
        return () => {
            // Limpiar timeout al desmontar
            debouncedRef.current.cancel();
        };
    }, []);
    
    return debouncedRef.current;
}

/**
 * Hook para debounce de valor de input
 * Útil para: búsquedas, filtros que hacen API calls
 * @param {string} initialValue - Valor inicial
 * @param {number} delay - Milisegundos a esperar
 * @returns {object} { value, debouncedValue, setValue }
 */
export function useDebouncedValue(initialValue = '', delay = 500) {
    const React = require('react');
    const [value, setValue] = React.useState(initialValue);
    const [debouncedValue, setDebouncedValue] = React.useState(initialValue);
    
    React.useEffect(() => {
        const handler = debounce(() => {
            setDebouncedValue(value);
        }, delay);
        
        handler();
        
        return () => handler.cancel();
    }, [value, delay]);
    
    return { value, setValue, debouncedValue };
}
